// src/api/axiosInstance.js
import axios from "axios";
import * as Sentry from "@sentry/react";
import endpoints from "./endpoints";

const AuthService = axios.create({
  baseURL: endpoints.API_ROOT,
  timeout: 60000,
  withCredentials: true,
});

const RefreshService = axios.create({
  baseURL: endpoints.API_ROOT,
  timeout: 15000,
  withCredentials: true,
});

const PublicService = axios.create({
  baseURL: endpoints.API_ROOT,
  timeout: 15000,
});

const isBrowser = typeof window !== "undefined";

const isAuthEndpoint = (url) => {
  if (!url) return false;
  const value = String(url);
  return [
    endpoints.LOGIN,
    endpoints.REFRESH,
    endpoints.LOGOUT,
    "/api/token/obtain/",
    "/api/token/refresh/",
    "/api/blacklist/",
  ].some((endpoint) => value.includes(endpoint));
};

const redirectToLogin = () => {
  if (!isBrowser) return;
  const currentPath = window.location.pathname || "";
  if (currentPath.startsWith("/login")) return;
  const next = `${window.location.pathname || "/"}${window.location.search || ""}${
    window.location.hash || ""
  }`;
  const target = `/login${next ? `?next=${encodeURIComponent(next)}` : ""}`;
  if (window.location.pathname + window.location.search + window.location.hash !== target) {
    window.location.assign(target);
  }
};

const annotateError = (error) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    error.isAuthError = true;
  }
  return error;
};

const captureServerError = (error) => {
  const status = error?.response?.status;
  if (status && status >= 500 && status <= 599) {
    Sentry.captureException(error);
  }
};

const redirectOnAuthError = (error) => {
  if (!error?.isAuthError) return;
  if (isAuthEndpoint(error?.config?.url)) return;
  redirectToLogin();
};

let accessToken = null;
const setAccessToken = (token) => {
  accessToken = token || null;
  if (accessToken) {
    AuthService.defaults.headers["Authorization"] = `JWT ${accessToken}`;
  } else {
    delete AuthService.defaults.headers["Authorization"];
  }
};

const getAccessToken = () => accessToken;

// Attach access token to requests
AuthService.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers["Authorization"] = `JWT ${token}`;
  return config;
});

let isRefreshing = false;
let queue = [];
let refreshPromise = null;

const processQueue = (error, token = null) => {
  queue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  queue = [];
};

const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await RefreshService.post(endpoints.REFRESH);
      const { access } = res.data || {};
      if (!access) throw new Error("No access token returned from refresh");
      setAccessToken(access);
      return access;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

AuthService.interceptors.response.use(
  (response) => response,
  async (error) => {
    captureServerError(error);
    annotateError(error);

    const originalRequest = error.config;

    // Only try refresh for 401s on non-auth endpoints, and only once
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest.url)
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers["Authorization"] = `JWT ${token}`;
          return AuthService(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const access = await refreshAccessToken();
        processQueue(null, access);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers["Authorization"] = `JWT ${access}`;
        return AuthService(originalRequest);
      } catch (err) {
        processQueue(err, null);
        setAccessToken(null);

        // NOW redirect (only after refresh failed)
        redirectToLogin();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    // Non-refreshable auth errors can redirect here
    redirectOnAuthError(error);
    return Promise.reject(error);
  }
);

const baseErrorInterceptor = (error) => {
  captureServerError(error);
  annotateError(error);
  redirectOnAuthError(error);
  return Promise.reject(error);
};

PublicService.interceptors.response.use((response) => response, baseErrorInterceptor);
RefreshService.interceptors.response.use((response) => response, baseErrorInterceptor);

export { AuthService, RefreshService, PublicService, setAccessToken, getAccessToken, refreshAccessToken };
export default AuthService;
